import React, { useEffect, useRef, useState } from 'react';

import { useRouteMatch, useLocation, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'react-translate';

import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import Title from '@components/Title';
import Button from '@components/Button';
import CustomModal from '@components/CustomModal/CustomModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SET_DATE_RANGE } from '@redux/generic/types';
import { GET_CURRENT, GET_CURRENT_ITEM, GET_CODE_INFORMATION } from '@redux/generic/types';
import CustomDateRange from './CustomDateRange';
import * as S from './styles';

function getStoreValues(state) {
  return {
    translatorReducer: state.translatorReducer,
    genericReducer: state.genericReducer,
  };
}

function ButtonCalendar() {
  const dateRangeRef = useRef(null);
  const [opened, setOpened] = useState(false);
  const [dateMarked, setDateMarked] = useState(null);
  const [isPreset, setIsPreset] = useState(false);
  const [itemSelected, setItemSelected] = useState({ datePreset: '' });

  const t = useTranslate('ButtonCalendar');
  const dispatch = useDispatch();
  const {
    params: { code, module },
  } = useRouteMatch();
  const location = useLocation();
  const { search } = location;
  const history = useHistory();
  const {
    translatorReducer,
    genericReducer: { current, currentItem },
  } = useSelector(getStoreValues);

  useEffect(() => {
    if (code === 'root') {
      dispatch({
        type: GET_CURRENT.REQUEST,
        version: '1.1',
      });
    } else {
      dispatch({
        type: GET_CODE_INFORMATION.REQUEST,
        version: '1.1',
        code,
      });

      dispatch({
        type: GET_CURRENT_ITEM.REQUEST,
        version: '1.1',
      });
    }
  }, [code, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentData =
      code === 'root' ? !!current.data && current.data : !!currentItem.data && currentItem.data;
    const datePreset = params.get('date_preset');
    let since = params.get('since');
    let until = params.get('until');

    if (!until || !since) {
      since = currentData.start_date;
      until = currentData.end_date;
      if (!datePreset && module === 'Overview') {
        history.push('?custom_date=true');
      }
    }
    if (currentData) {
      if (datePreset) {
        setDateMarked(datePreset.toUpperCase());
        setIsPreset(true);

        if (datePreset === 'last_7d') {
          currentData.end_date = dayjs(currentData.end_date).subtract(1, 'day');
          currentData.start_date = dayjs(currentData.start_date).subtract(1, 'day');
        }

        dispatch({
          type: SET_DATE_RANGE.REQUEST,
          update: true,
          currentData,
          period: {
            datePreset: datePreset.toUpperCase(),
          },
        });
      } else if (until && since) {
        setDateMarked(`${since} ${until}`);
        setIsPreset(false);
        dispatch({
          type: SET_DATE_RANGE.REQUEST,
          update: true,
          currentData,
          period: {
            startDate: since,
            endDate: until,
          },
        });
      }
    }
  }, [
    current.data,
    search,
    current.isLoading,
    currentItem.isLoading,
    location.search,
    code,
    currentItem.data,
    module,
    history,
    dispatch,
  ]);

  useEffect(() => {
    if (translatorReducer.language === 'pt-BR') {
      dayjs.locale('pt');
    } else if (translatorReducer.language === 'en-US') {
      dayjs.locale('en');
    }
  }, [translatorReducer]);

  function openModal() {
    setOpened(true);
  }

  function closeModal() {
    setOpened(false);
  }

  function saveDateRange(selectedSearch) {
    let searchToPush;

    if (selectedSearch.datePreset) {
      searchToPush = `date_preset=${selectedSearch.datePreset.toLowerCase()}`;
    } else {
      const since = dayjs(selectedSearch.start).format('YYYY-MM-DD');
      const until = dayjs(selectedSearch.end).format('YYYY-MM-DD');
      searchToPush = `since=${since}&until=${until}`;
      if (selectedSearch.isCustomOption === true) {
        searchToPush += '&custom_date=true';
      }
    }

    searchToPush = verifyOtherParams(searchToPush);

    if (!history.location.search.includes(searchToPush)) {
      history.push({
        search: searchToPush,
      });
    }

    closeModal();
  }

  function verifyOtherParams(searchToPush) {
    const urlParams = new URLSearchParams(location.search);

    for (const item of urlParams) {
      if (
        item[0] !== 'since' &&
        item[0] !== 'until' &&
        item[0] !== 'date_preset' &&
        item[0] !== 'custom_date'
      ) {
        searchToPush += `&${item[0]}=${item[1]}`;
      }
    }

    return searchToPush;
  }

  function handleSelect(select) {
    dateRangeRef.current.focus();
    setItemSelected(select);
  }

  return (
    <>
      {dateMarked && (
        <S.ButtonStyled onClick={openModal}>
          <S.IconStyled>
            <FontAwesomeIcon icon={['far', 'calendar-alt']} />
          </S.IconStyled>
          <S.DateTextStyled>
            {isPreset ? (
              t(dateMarked.toUpperCase())
            ) : (
              <>
                {dayjs(dateMarked.split(' ')[0]).format('ll')} —{' '}
                {dayjs(dateMarked.split(' ')[1]).format('ll')}
              </>
            )}
          </S.DateTextStyled>
        </S.ButtonStyled>
      )}
      {opened && (
        <CustomModal
          visible={opened}
          effect="fadeInDown"
          width={window.innerWidth > 800 ? '50%' : '100%'}
          onClickAway={closeModal}
        >
          <S.TitleStyle>
            <Title tag="h2" color="blue2">
              {t('TITLE-DATE-RANGE')}
            </Title>
          </S.TitleStyle>
          <CustomDateRange
            handleSelectButtonCalendar={handleSelect}
            closeModal={closeModal}
            saveDateRange={saveDateRange}
            opened={opened}
          >
            <S.ButtonApply>
              <Button
                innerRef={dateRangeRef}
                backgroundColor="blue2"
                onClick={() => {
                  saveDateRange(itemSelected);
                }}
              >
                {t('APPLY')}
              </Button>
            </S.ButtonApply>
          </CustomDateRange>
        </CustomModal>
      )}
    </>
  );
}

export default ButtonCalendar;
